import type { EntryExample } from '@/db/schema';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const MAX_IMAGE_WIDTH = 970;
export const MIN_IMAGE_WIDTH = 10;
export const SMALL_IMAGE_WIDTH = 250;

export const DEFAULT_EDITOR_OPTIONS = {
    minimap: { enabled: false },
    lineNumbers: 'off' as const,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on' as const,
};

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const escapeXml = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

export function generateImgTag(
    htmlContent: {
        text: string;
        attributes?: Array<{ type: string; [key: string]: unknown }>;
    },
    { subject, releaseId }: { subject: string; releaseId: string },
): string {
    const imageAttrs = (htmlContent.attributes?.[0] || {}) as Record<string, any>;
    const imgId = imageAttrs.image_ids?.[0];
    const imgSrc = `https://zytools.zybooks.com/zyAuthor/${subject}/${releaseId}/IMAGES/${imgId}`;
    const altText = escapeXml(imageAttrs.alt_text || htmlContent.text || 'Image');
    const width = imageAttrs.width ?? null;
    const height = imageAttrs.height ?? null;
    const inlineMarker = imageAttrs.type === 'inline-image' ? ' data-inline-image="true"' : '';
    const pull = imageAttrs.pull === 'left' || imageAttrs.pull === 'right' ? imageAttrs.pull : null;
    const pullAttr = pull ? ` data-pull="${pull}"` : '';
    const dimensionAttrs = [width != null ? `width="${width}"` : '', height != null ? `height="${height}"` : ''].filter(Boolean).join(' ');
    const style = [pull ? `float: ${pull};` : '', width != null ? `max-width: ${width};` : '', height != null ? `height: ${height};` : '']
        .filter(Boolean)
        .join(' ');
    return `<img id="${imgId}" src="${imgSrc}" alt="${altText}"${dimensionAttrs ? ` ${dimensionAttrs}` : ''}${inlineMarker}${pullAttr} style="${style}" />`;
}

export const unescapeXml = (text: string): string =>
    text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&');

export const formatIndentedTextForHtml = (text: string): string => {
    const withBreaks = text.replace(/\r\n?/g, '\n').replace(/\n/g, '<br>');

    return withBreaks.replace(/(^|<br\s*\/?>)([ \t]+)/g, (_match, lineStart, indent) => {
        const preservedIndent = indent.replace(/ /g, '&nbsp;').replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
        return `${lineStart}${preservedIndent}`;
    });
};

/** XML predefined entities — leave as-is so `DOMParser` (`text/xml`) can resolve them. */
const XML_PREDEFINED_ENTITY_NAMES = new Set(['amp', 'lt', 'gt', 'quot', 'apos']);

/**
 * Maps HTML-style named character references to Unicode. Covers common typography, Latin-1,
 * symbols, and Greek letters often pasted from authoring tools. Unknown names are left unchanged.
 *
 * Used before strict XML parsing: XML does not define `&mdash;`, `&nbsp;`, etc. without a DTD.
 */
const COMMON_HTML_NAMED_ENTITIES: Record<string, string> = {
    nbsp: '\u00A0',
    iexcl: '\u00A1',
    cent: '\u00A2',
    pound: '\u00A3',
    curren: '\u00A4',
    yen: '\u00A5',
    brvbar: '\u00A6',
    sect: '\u00A7',
    uml: '\u00A8',
    copy: '\u00A9',
    ordf: '\u00AA',
    laquo: '\u00AB',
    not: '\u00AC',
    shy: '\u00AD',
    reg: '\u00AE',
    macr: '\u00AF',
    deg: '\u00B0',
    plusmn: '\u00B1',
    sup2: '\u00B2',
    sup3: '\u00B3',
    acute: '\u00B4',
    micro: '\u00B5',
    para: '\u00B6',
    middot: '\u00B7',
    cedil: '\u00B8',
    sup1: '\u00B9',
    ordm: '\u00BA',
    raquo: '\u00BB',
    frac14: '\u00BC',
    frac12: '\u00BD',
    frac34: '\u00BE',
    iquest: '\u00BF',
    Agrave: '\u00C0',
    Aacute: '\u00C1',
    Acirc: '\u00C2',
    Atilde: '\u00C3',
    Auml: '\u00C4',
    Aring: '\u00C5',
    AElig: '\u00C6',
    Ccedil: '\u00C7',
    Egrave: '\u00C8',
    Eacute: '\u00C9',
    Ecirc: '\u00CA',
    Euml: '\u00CB',
    Igrave: '\u00CC',
    Iacute: '\u00CD',
    Icirc: '\u00CE',
    Iuml: '\u00CF',
    ETH: '\u00D0',
    Ntilde: '\u00D1',
    Ograve: '\u00D2',
    Oacute: '\u00D3',
    Ocirc: '\u00D4',
    Otilde: '\u00D5',
    Ouml: '\u00D6',
    times: '\u00D7',
    Oslash: '\u00D8',
    Ugrave: '\u00D9',
    Uacute: '\u00DA',
    Ucirc: '\u00DB',
    Uuml: '\u00DC',
    Yacute: '\u00DD',
    THORN: '\u00DE',
    szlig: '\u00DF',
    agrave: '\u00E0',
    aacute: '\u00E1',
    acirc: '\u00E2',
    atilde: '\u00E3',
    auml: '\u00E4',
    aring: '\u00E5',
    aelig: '\u00E6',
    ccedil: '\u00E7',
    egrave: '\u00E8',
    eacute: '\u00E9',
    ecirc: '\u00EA',
    euml: '\u00EB',
    igrave: '\u00EC',
    iacute: '\u00ED',
    icirc: '\u00EE',
    iuml: '\u00EF',
    eth: '\u00F0',
    ntilde: '\u00F1',
    ograve: '\u00F2',
    oacute: '\u00F3',
    ocirc: '\u00F4',
    otilde: '\u00F5',
    ouml: '\u00F6',
    divide: '\u00F7',
    oslash: '\u00F8',
    ugrave: '\u00F9',
    uacute: '\u00FA',
    ucirc: '\u00FB',
    uuml: '\u00FC',
    yacute: '\u00FD',
    thorn: '\u00FE',
    yuml: '\u00FF',
    OElig: '\u0152',
    oelig: '\u0153',
    Scaron: '\u0160',
    scaron: '\u0161',
    Yuml: '\u0178',
    fnof: '\u0192',
    circ: '\u02C6',
    tilde: '\u02DC',
    Alpha: '\u0391',
    Beta: '\u0392',
    Gamma: '\u0393',
    Delta: '\u0394',
    Epsilon: '\u0395',
    Zeta: '\u0396',
    Eta: '\u0397',
    Theta: '\u0398',
    Iota: '\u0399',
    Kappa: '\u039A',
    Lambda: '\u039B',
    Mu: '\u039C',
    Nu: '\u039D',
    Xi: '\u039E',
    Omicron: '\u039F',
    Pi: '\u03A0',
    Rho: '\u03A1',
    Sigma: '\u03A3',
    Tau: '\u03A4',
    Upsilon: '\u03A5',
    Phi: '\u03A6',
    Chi: '\u03A7',
    Psi: '\u03A8',
    Omega: '\u03A9',
    alpha: '\u03B1',
    beta: '\u03B2',
    gamma: '\u03B3',
    delta: '\u03B4',
    epsilon: '\u03B5',
    zeta: '\u03B6',
    eta: '\u03B7',
    theta: '\u03B8',
    iota: '\u03B9',
    kappa: '\u03BA',
    lambda: '\u03BB',
    mu: '\u03BC',
    nu: '\u03BD',
    xi: '\u03BE',
    omicron: '\u03BF',
    pi: '\u03C0',
    rho: '\u03C1',
    sigmaf: '\u03C2',
    sigma: '\u03C3',
    tau: '\u03C4',
    upsilon: '\u03C5',
    phi: '\u03C6',
    chi: '\u03C7',
    psi: '\u03C8',
    omega: '\u03C9',
    thetasym: '\u03D1',
    upsih: '\u03D2',
    piv: '\u03D6',
    ensp: '\u2002',
    emsp: '\u2003',
    thinsp: '\u2009',
    zwnj: '\u200C',
    zwj: '\u200D',
    lrm: '\u200E',
    rlm: '\u200F',
    ndash: '\u2013',
    mdash: '\u2014',
    lsquo: '\u2018',
    rsquo: '\u2019',
    sbquo: '\u201A',
    ldquo: '\u201C',
    rdquo: '\u201D',
    bdquo: '\u201E',
    dagger: '\u2020',
    Dagger: '\u2021',
    bull: '\u2022',
    hellip: '\u2026',
    permil: '\u2030',
    prime: '\u2032',
    Prime: '\u2033',
    lsaquo: '\u2039',
    rsaquo: '\u203A',
    oline: '\u203E',
    frasl: '\u2044',
    euro: '\u20AC',
    image: '\u2111',
    weierp: '\u2118',
    real: '\u211C',
    trade: '\u2122',
    alefsym: '\u2135',
    larr: '\u2190',
    uarr: '\u2191',
    rarr: '\u2192',
    darr: '\u2193',
    harr: '\u2194',
    crarr: '\u21B5',
    lArr: '\u21D0',
    uArr: '\u21D1',
    rArr: '\u21D2',
    dArr: '\u21D3',
    hArr: '\u21D4',
    forall: '\u2200',
    part: '\u2202',
    exist: '\u2203',
    empty: '\u2205',
    nabla: '\u2207',
    isin: '\u2208',
    notin: '\u2209',
    ni: '\u220B',
    prod: '\u220F',
    sum: '\u2211',
    minus: '\u2212',
    lowast: '\u2217',
    radic: '\u221A',
    prop: '\u221D',
    infin: '\u221E',
    ang: '\u2220',
    and: '\u2227',
    or: '\u2228',
    cap: '\u2229',
    cup: '\u222A',
    int: '\u222B',
    there4: '\u2234',
    sim: '\u223C',
    cong: '\u2245',
    asymp: '\u2248',
    ne: '\u2260',
    equiv: '\u2261',
    le: '\u2264',
    ge: '\u2265',
    sub: '\u2282',
    sup: '\u2283',
    nsub: '\u2284',
    sube: '\u2286',
    supe: '\u2287',
    oplus: '\u2295',
    otimes: '\u2297',
    perp: '\u22A5',
    sdot: '\u22C5',
    lceil: '\u2308',
    rceil: '\u2309',
    lfloor: '\u230A',
    rfloor: '\u230B',
    lang: '\u2329',
    rang: '\u232A',
    loz: '\u25CA',
    spades: '\u2660',
    clubs: '\u2663',
    hearts: '\u2665',
    diams: '\u2666',
};

/**
 * Replaces common HTML named entities with Unicode so strict XML parsers accept the string.
 * Does not expand XML predefined entities (`&amp;`, `&lt;`, …) or unknown names.
 */
export function replaceCommonHtmlNamedEntities(input: string): string {
    return input.replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (full, name: string) => {
        const key = name.toLowerCase();
        if (XML_PREDEFINED_ENTITY_NAMES.has(key)) {
            return full;
        }
        const resolved = COMMON_HTML_NAMED_ENTITIES[name] ?? COMMON_HTML_NAMED_ENTITIES[key];
        return resolved !== undefined ? resolved : full;
    });
}

/** Repairs common legacy zy XML issues before strict XML parsing. */
export function cleanZyXML(xml: string): string {
    // Handle invalid XML: HTML entities and poorly structured attributes
    // See - https://github.com/zyBooks/zybertron/blob/main/platform/zycommon-web/addon/utils/convert-animation-xml-to-json.js#L670
    return replaceCommonHtmlNamedEntities(xml.replaceAll('""Comic Sans MS""', '"Comic Sans MS"'));
}

/**
 * Removes all HTML named entities from the input string.
 *
 * @param input - The input string to remove HTML named entities from.
 * @returns The input string with all HTML named entities removed.
 */
export function removeAllHtmlNamedEntities(input: string): string {
    return input.replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, '');
}

const AMP_NOT_XML_REF = /&(?!#[0-9]+;|#[xX][0-9a-fA-F]+;|[a-zA-Z][a-zA-Z0-9]*;)/g;

function escapeAmpersandsInAttributeValueFragment(value: string): string {
    return value.replace(AMP_NOT_XML_REF, '&amp;');
}

/**
 * Escapes bare `&` only inside quoted values for the given attribute names (e.g. `link`, `src`).
 * Avoids touching element text or code-tag bodies, so later `escapeXml` passes do not
 * double-encode those regions.
 *
 * Other attributes that often carry URLs with query strings and may need the same treatment:
 * `href`, `poster`, `data-src`, `data-url`, `cite`, `longdesc`, `usemap`, `formaction`, `icon`,
 * `manifest`, `profile`, `archive`, `background` (legacy), `dynsrc` (legacy).
 */
export function escapeBareAmpersandsInXmlAttributeValues(input: string, attributeNames: readonly string[]): string {
    let result = input;
    for (const rawName of attributeNames) {
        const namePattern = rawName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const doubleQuoted = new RegExp(`(\\b${namePattern}\\s*=\\s*)(")([^"]*)(")`, 'gi');
        result = result.replace(doubleQuoted, (_m, prefix: string, q1: string, value: string, q2: string) => {
            return `${prefix}${q1}${escapeAmpersandsInAttributeValueFragment(value)}${q2}`;
        });
        const singleQuoted = new RegExp(`(\\b${namePattern}\\s*=\\s*)(')([^']*)(')`, 'gi');
        result = result.replace(singleQuoted, (_m, prefix: string, q1: string, value: string, q2: string) => {
            return `${prefix}${q1}${escapeAmpersandsInAttributeValueFragment(value)}${q2}`;
        });
    }
    return result;
}

export const escapeFilename = (inputString: string): string => {
    // Replace invalid characters with underscore
    const invalidCharsRegex = /[<>:"/\\|?*]/g;
    let escapedString = inputString.replace(invalidCharsRegex, '_');

    // Replace spaces with underscore and remove dots
    escapedString = escapedString.replace(/ /g, '_');
    escapedString = escapedString.replace(/\./g, '');

    return escapedString;
};

export const formatFolderName = (number: number, name: string): string => {
    // Pad number with leading zero if needed and combine with escaped name
    return `${number.toString().padStart(2, '0')}_${escapeFilename(name)}`;
};

export const omitKey = (obj: any, keys: string[]) => {
    const newObj = { ...obj };
    keys.forEach(key => {
        delete newObj[key];
    });
    return newObj;
};

export function isMissingZytagSnippetError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err);
    return /No zytag snippet found/i.test(message) || /No XML found in tag index/i.test(message);
}

export async function getXmlFromTag(
    bookName: string,
    id: string,
    chapter: string,
    fetchSnippet: (args: {
        zybook_code: string;
        guid: string;
        chapter_number: string;
    }) => Promise<{ record?: { snippet?: string } | null }>,
    removeIdFromXML = true,
): Promise<string> {
    if (!bookName || !id || !chapter) {
        throw new Error(`Missing required arguments to getXmlFromTag: bookName="${bookName}", id="${id}", chapter="${chapter}"`);
    }

    // TODO: Remove once we're exporting to real zybooks, and not appending -MA to GUIDs.
    id = id.replace(/-MA(_\d+)?$/, '');

    const uuidPrefix = id.match(/^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
    if (uuidPrefix) {
        id = uuidPrefix[1]!;
    }

    const key = `${bookName}:${id}:${chapter}`;

    const response = await fetchSnippet({
        zybook_code: bookName,
        guid: id,
        chapter_number: chapter,
    });
    const record = response.record;

    if (!record || !record.snippet) {
        throw new Error(`No XML found in tag index for key: "${key}"`);
    }

    const snippet = record.snippet ?? '';
    return removeIdFromXML ? removeAttributeFromXml(snippet, 'id') : snippet;
}

export function removeAttributeFromXml(xml: string, attributeName: string): string {
    const regex = new RegExp(`\\s${attributeName}="[^"]*"`);
    return xml.replace(regex, '');
}

export function detectLanguageFromCodeSnippet(code: string): string {
    // Detect language from the content or default to plaintext
    if (code.includes('public class') || code.includes('private class')) {
        return 'java';
    } else if (code.includes('def ') || code.includes('import ')) {
        return 'python';
    } else if (code.includes('#include') || code.includes('std::')) {
        return 'cpp';
    } else if (code.includes('function') || code.includes('const ') || code.includes('let ')) {
        return 'javascript';
    }
    return 'plaintext';
}

export function toTitleCase(str: string) {
    return str
        .toLowerCase()
        .split(' ')
        .map(word => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
}

export function sortObjectKeys(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(sortObjectKeys);
    }
    if (obj !== null && typeof obj === 'object') {
        const sorted: Record<string, any> = {};
        for (const key of Object.keys(obj).sort()) {
            sorted[key] = sortObjectKeys(obj[key]);
        }
        return sorted;
    }
    return obj;
}

/**
 * Fields that directly affect UI rendering and should be compared.
 * These fields control what users see and how content is displayed:
 * - type/resource_type: Determines which component renders the content
 * - payload: Contains the actual content (HTML, questions, choices, etc.)
 * - caption: Visible caption text displayed above/below content
 * - instructions: Visible instruction text for activities
 * - parts: Number of parts in activities (affects display)
 * - width/height: Visual sizing dimensions
 * - activity_type: Affects activity rendering (participation vs challenge)
 *
 * Fields NOT compared (metadata that doesn't affect UI):
 * - id, guid, gdoc_id, parent_gdoc_id: Internal identifiers
 * - release_id, property_overrides: Metadata
 * - index_items: Indexing metadata
 */
const UI_CRITICAL_FIELDS = [
    'type',
    'resource_type',
    'caption',
    'instructions',
    'payload',
    'parts',
    'height',
    'width',
    'activity_type',
] as const;

export function extractUICriticalFields(contentResource: any): any {
    if (!contentResource || typeof contentResource !== 'object') {
        return contentResource;
    }

    const extracted: Record<string, any> = {};

    for (const field of UI_CRITICAL_FIELDS) {
        if (field in contentResource) {
            extracted[field] = contentResource[field];
        }
    }

    return extracted;
}

export function extractUICriticalFieldsFromArray(contentResources: any[]): any[] {
    return contentResources.map(extractUICriticalFields);
}

export async function getSnippetsForTag(
    tagName: string,
    loadRelationshipTags: () => Promise<Array<{ parent: string; entries: EntryExample[] }>>,
    limit = 10,
): Promise<EntryExample[]> {
    const relationshipTags = (await loadRelationshipTags()).filter(rt => rt.parent.toLowerCase() === tagName.toLowerCase());

    const allEntries: EntryExample[] = [];
    for (const rt of relationshipTags) {
        allEntries.push(...rt.entries);
    }

    return allEntries.slice(0, limit);
}

export const alphabetEnumerate = (i: number): string => {
    let letters = '';
    let index = i;
    while (index > -1) {
        letters = String.fromCharCode(97 + (index % 26)) + letters;
        const quotient = Math.floor(index / 26);
        index = quotient > 0 ? quotient - 1 : -1;
    }
    return letters;
};

export function setMathMLDisplayAttribute(mathml: string, isBlockMath: boolean): string {
    const displayValue = isBlockMath ? 'block' : 'inline';
    const mathTagRegex = /<math([^>]*)>/;
    const match = mathml.match(mathTagRegex);

    if (!match) return mathml;
    const attributes = match[1];
    const hasDisplay = /\sdisplay=/.test(attributes);

    if (hasDisplay) {
        return mathml.replace(/(<math[^>]*\sdisplay=")([^"]*)(")/, `$1${displayValue}$3`);
    } else {
        return mathml.replace(/<math([^>]*)>/, `<math$1 display="${displayValue}">`);
    }
}

export const MAX_FILE_SIZE = 200 * 1024; // 200KB
export const ACCEPTED_IMAGE_TYPES = 'image/png, image/jpg, image/jpeg, image/gif, image/svg+xml';

/**
 * Normalizes whitespace inside inline LaTeX delimiters (`\(...\)`).
 *
 * Trims leading/trailing whitespace and collapses consecutive whitespace
 * characters to a single space within each `\(...\)` expression.
 * Content outside of inline LaTeX delimiters is left unchanged.
 *
 * @example
 * cleanInlineLatex("\\(  x^2  +  y^2  \\)") // "\\(x^2 + y^2\\)"
 */
export function cleanInlineLatex(text: string) {
    return text.replace(/\\\(([\s\S]*?)\\\)/g, (_match, latex) => {
        const cleaned = latex.trim().replace(/\s+/g, ' ');
        return `\\(${cleaned}\\)`;
    });
}

/**
 * Some block math is incorrectly wrapped with empty inline math delimiters (\(\))
 * instead of proper block math ($$...$$). We detect and replace these with `<p>$$...$$</p>` for correct rendering.
 *
 * All occurrences are replaced. The `$$` signs are encoded as
 * `&dollar;&dollar;` so they survive HTML parsing.
 *
 * @example
 * cleanMalformedBlockMath("\\(\\)x^2 + y^2\\(\\)")
 * // '<p>&dollar;&dollar;x^2 + y^2&dollar;&dollar;</p>'
 */
export function cleanMalformedBlockMath(text: string) {
    return text.replace(/\\\(\\\)[\s\S]*?\\\(\\\)/g, match => {
        const replacedBlock = match.replace(/\\\(\\\)/g, '&dollar;&dollar;');
        return `<p>${replacedBlock}</p>`;
    });
}

// All HTML block tag names
const BLOCK_TAG_NAMES = new Set([
    'div',
    'p',
    'ul',
    'ol',
    'blockquote',
    'pre',
    'figure',
    'table',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'address',
    'article',
    'aside',
    'footer',
    'header',
    'main',
    'nav',
    'section',
]);

function hasBlockDescendant(element: Element): boolean {
    for (const child of element.children) {
        if (BLOCK_TAG_NAMES.has(child.tagName.toLowerCase())) return true;
        if (hasBlockDescendant(child)) return true;
    }
    return false;
}

const BLOCK_MATH_PATTERN = /\$\$[\s\S]*?\$\$/;

function hasBlockMathInText(element: Element): boolean {
    const text = element.textContent ?? '';
    return BLOCK_MATH_PATTERN.test(text);
}

/**
 * Parses HTML and adds data-definition-mode="block" to span[data-definition="true"]
 * elements that contain block content (block descendants like div, p, ul, ol, etc., or
 * block LaTeX $$...$$), so they are parsed as zydefnBlock instead of inline zydefn.
 * Assumes valid HTML (due to DOMParser); invalid markup (e.g. block elements inside span)
 * may be normalized by the browser before detection.
 */
export function markZydefnBlocksInHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const spans = doc.body.querySelectorAll('span[data-definition="true"]');
    for (const span of spans) {
        if (hasBlockDescendant(span) || hasBlockMathInText(span)) {
            span.setAttribute('data-definition-mode', 'block');
        }
    }
    return doc.body.innerHTML;
}

/**
 * Removes any version suffix from a UUID.
 * @param uuid - The UUID to clean.
 * @returns The cleaned UUID.
 */
export function getCleanUuid(uuid: string): string {
    let cleanedUuid = uuid;
    const uuidPrefix = uuid.match(/^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
    if (uuidPrefix) {
        cleanedUuid = uuidPrefix[1]!;
    }
    return cleanedUuid;
}

/**
 * Function that processes a chapter/section title for dynamic content such as ZYFUNCTION
 */
export function processTitle(text: string, language: string): string {
    return text.replace(/<span class="inline-version">([^:<]+):([\s\S]*?)<\/span>/g, (_match, spanLanguage: string, content: string) => {
        return spanLanguage === language ? content : '';
    });
}
